import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookmarkIcon, MagnifyingGlassIcon, Squares2X2Icon, ViewColumnsIcon } from '@heroicons/react/24/outline';
import ContractCard from '../components/ContractCard';
import ContractFilters from '../components/ContractFilters';
import ContractTable from '../components/ContractTable';
import LoadingSpinner from '../components/LoadingSpinner';
import SEO from '../components/SEO';
import { useOpportunities } from '../contexts/OpportunityContext';
import { useSavedOpportunities } from '../contexts/SavedOpportunitiesContext';
import { savedOpportunityAPI } from '../services/api';
import { Contract } from '../types';
import { getComparableDueValue } from '../utils/contractFormatting';
import { opportunityMatchesFilters } from '../utils/opportunityFilters';
import { trackEvent } from '../utils/analytics';
import { DEFAULT_REGION, buildRegionPath, useActiveRegion } from '../regions';

type SavedView = 'active' | 'past' | 'all';

const isPastOpportunity = (opportunity: Contract) =>
  Boolean(opportunity.is_closed) || (typeof opportunity.days_until_due === 'number' && opportunity.days_until_due < 0);

const SavedOpportunities: React.FC = () => {
  const region = useActiveRegion() ?? DEFAULT_REGION;
  const { viewMode, setViewMode } = useOpportunities();
  const { isSaved, setSavedState } = useSavedOpportunities();
  const [opportunities, setOpportunities] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savedView, setSavedView] = useState<SavedView>('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('due_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [filters, setFilters] = useState({
    industries: [] as string[],
    opportunityTypes: [] as string[],
    procuringTypes: [] as string[],
    preBidMeeting: [] as string[],
    procuringOrganizations: [] as string[],
  });

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const response = await savedOpportunityAPI.list();
        if (!active) return;
        const items = response.data.opportunities ?? [];
        setOpportunities(items);
        items.forEach((item) => item.id && setSavedState(item.id, true));
        if (items.length > 0 && items.every(isPastOpportunity)) setSavedView('all');
        trackEvent('saved_opportunities_viewed', { region, total: items.length });
      } catch {
        if (active) setError('Unable to load Saved Opportunities right now.');
      } finally {
        if (active) setLoading(false);
      }
    };
    void load();
    return () => { active = false; };
  }, [region, setSavedState]);

  const currentlySaved = useMemo(
    () => opportunities.filter((opportunity) => isSaved(opportunity)),
    [isSaved, opportunities],
  );

  const pastCount = useMemo(() => currentlySaved.filter(isPastOpportunity).length, [currentlySaved]);

  const opportunitiesInSavedView = useMemo(
    () => currentlySaved.filter((opportunity) => {
      const past = isPastOpportunity(opportunity);
      if (savedView === 'active') return !past;
      if (savedView === 'past') return past;
      return true;
    }),
    [currentlySaved, savedView],
  );

  const filtered = useMemo(() => {
    const rows = opportunitiesInSavedView.filter((opportunity) =>
      opportunityMatchesFilters(opportunity, { ...filters, searchTerm })
    );

    return [...rows].sort((a, b) => {
      const direction = sortDirection === 'asc' ? 1 : -1;
      if (sortField === 'due_date') {
        const aTime = new Date(getComparableDueValue(a) || '9999-12-31').getTime();
        const bTime = new Date(getComparableDueValue(b) || '9999-12-31').getTime();
        return (aTime - bTime) * direction;
      }
      const key = sortField === 'city' ? 'procuring_organization' : sortField;
      const aValue = String(a[key as keyof Contract] ?? '').toLowerCase();
      const bValue = String(b[key as keyof Contract] ?? '').toLowerCase();
      return aValue.localeCompare(bValue) * direction;
    });
  }, [filters, opportunitiesInSavedView, searchTerm, sortDirection, sortField]);

  const handleSort = (field: string) => {
    if (field === sortField) setSortDirection((current) => current === 'asc' ? 'desc' : 'asc');
    else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  if (loading) return <LoadingSpinner message="Loading Saved Opportunities..." />;

  return (
    <div className="space-y-8">
      <SEO title="Saved Opportunities – OppFoundry" description="Review opportunities you saved in OppFoundry." />
      <div>
        <h1 className="text-3xl font-semibold text-gray-900">Saved Opportunities</h1>
        <p className="mt-3 text-base text-gray-600">Keep track of opportunities you want to review or pursue.</p>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
          {error}
          <button type="button" onClick={() => window.location.reload()} className="ml-3 font-semibold underline">Try again</button>
        </div>
      ) : currentlySaved.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center">
          <BookmarkIcon className="mx-auto h-12 w-12 text-slate-300" />
          <h2 className="mt-4 text-lg font-semibold text-slate-900">No saved opportunities yet</h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">Save opportunities while browsing to keep the strongest leads in one place.</p>
          <Link to={buildRegionPath(region, 'opportunities')} className="mt-6 inline-flex rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-500">Browse opportunities</Link>
        </div>
      ) : (
        <>
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div className="relative w-full max-w-xl">
                  <label htmlFor="saved-search" className="mb-2 block text-sm font-medium text-gray-700">Search saved opportunities</label>
                  <MagnifyingGlassIcon className="pointer-events-none absolute bottom-3 left-3 h-5 w-5 text-gray-400" />
                  <input id="saved-search" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="Search saved opportunities..." className="h-12 w-full rounded-lg border border-gray-300 pl-10 pr-4 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
                <div className="inline-flex w-fit rounded-full border border-gray-200 bg-gray-50 p-1">
                  {(['active', 'past', 'all'] as SavedView[]).map((value) => (
                    <button key={value} type="button" onClick={() => setSavedView(value)} className={`rounded-full px-4 py-2 text-sm font-semibold ${savedView === value ? 'bg-blue-600 text-white' : 'text-gray-600 hover:text-gray-900'}`}>
                      {value === 'active' ? 'Active' : value === 'past' ? 'Past / closed' : 'All'}
                    </button>
                  ))}
                </div>
              </div>
              <ContractFilters
                opportunitiesOverride={opportunitiesInSavedView}
                appliedFilters={{ ...filters, searchTerm }}
                onFilterChange={setFilters}
              />
            </div>
          </div>

          {pastCount > 0 && savedView === 'active' && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              {pastCount} saved {pastCount === 1 ? 'opportunity has' : 'opportunities have'} passed the deadline or closed. View {pastCount === 1 ? 'it' : 'them'} under Past / closed.
            </div>
          )}

          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-500">View:</span>
              <div className="flex rounded-full border border-gray-200 bg-white p-1">
                <button type="button" onClick={() => setViewMode('table')} className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium ${viewMode === 'table' ? 'bg-blue-600 text-white' : 'text-gray-600'}`}><ViewColumnsIcon className="h-4 w-4" />Table</button>
                <button type="button" onClick={() => setViewMode('cards')} className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium ${viewMode === 'cards' ? 'bg-blue-600 text-white' : 'text-gray-600'}`}><Squares2X2Icon className="h-4 w-4" />Cards</button>
              </div>
            </div>
            <span className="text-sm text-gray-500">{filtered.length} saved {filtered.length === 1 ? 'opportunity' : 'opportunities'}</span>
          </div>

          {filtered.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
              <h2 className="font-semibold text-slate-900">No saved opportunities match these filters</h2>
              <button type="button" onClick={() => { setSearchTerm(''); setFilters({ industries: [], opportunityTypes: [], procuringTypes: [], preBidMeeting: [], procuringOrganizations: [] }); }} className="mt-3 text-sm font-semibold text-blue-600 hover:text-blue-500">Clear filters</button>
            </div>
          ) : viewMode === 'table' ? (
            <ContractTable contracts={filtered} onSort={handleSort} sortField={sortField} sortDirection={sortDirection} saveSurface="saved_page" />
          ) : (
            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">{filtered.map((opportunity) => <ContractCard key={opportunity.id} contract={opportunity} saveSurface="saved_page" />)}</div>
          )}
        </>
      )}
    </div>
  );
};

export default SavedOpportunities;
